<template>
  <div class="journey_layout clearfix"
    :class="[{
      minimized: gnbMinimized < 0,
      expanded: gnbMinimized > 1,
      grouping: groupState
    }]">
    <report-header
      class="header-wrap layout-transition-up"
      v-if="!domainLoading"
      :domains="domains"
      @onLoading="onLoading"
      @setSegmentPayment="setSegmentPayment"
    />

    <div v-if="!initialLoading" class="view-wrap layout-transition-fade">
      <router-view class="content-wrap" />
      <transition name="fade">
        <div class="loading--mask" v-show="loading || initialLoading"></div>
      </transition>
    </div>
    <transition name="fade">
      <div class="loading--wrap" v-if="(loading || initialLoading) && !journeyPluck">
        <dimmed>
          <report-loading />
        </dimmed>
      </div>
    </transition>

    <chat-container v-show="showAiDocent" />

    <transition name="fade">
      <dimmed v-if="!!bookmarkListLayerOptions">
        <ba-bookmark-list-layer :mode="bookmarkListLayerOptions.mode"
                                :selectedUrlId="bookmarkListLayerOptions.gUrlId"
                                :isBlank="bookmarkListLayerOptions.blank" />
      </dimmed>
    </transition>

    <dimmed v-if="settingUtm">
      <marketing :utmCache="utmCache"
                :tag="settingUtm"
                :sessionCount="latestSessionCount"
                :sessionTotalCount="latestSessionTotalCount"
                @onSetUtm="onSetUtm"
                @onSetSurvey="onSetSurvey"
                @onCloseLayer="closeMarketing"
      />
    </dimmed>

    <dimmed v-if="isGivingChanceToChangeDateForTargeting">
      <alert :title="$t('journey.targeting.changeDate.title')"
            :description="givingChanceToChangeDateDescription"
            :cancel="$t('common.cancel')"
            :submit="$t('common.change')"
            @onSubmit="onChangeDateToTargetable"
            @onCancel="onCancelChangeDateToTargetable"
      />
    </dimmed>

    <Dimmed v-if="setPayment" :close-to-click="true" @close="setPayment = false" >
      <AiReportPayment
          :report-info="reportInfoForAIReport"
          :report-name="$t(`aiReport.segments.title.${reportView}`)"
          :plan-info="getPlan()[0]"
          :segment-meta-list="segmentMetaList"
          :set-list = setList()
          report-type="segment"
          @checkDuplicate="checkDuplicate"
          @payment="requestPayment"
          @close="setPayment = false; "
          @onChargeJam="onChargeJam"
      />
    </Dimmed>

    <dimmed v-if="openTossView" :style="{'z-index': 999999}">
      <checkout-view class="toss-view"
        :price="tossPrice"
        :serviceType="'ba'"
        :user="userData"
        :variantKey="variantKey"
        :product="'segment'"
        @cancelTossPayment="cancelTossPayment"
        @successTossPayment="successTossPayment"/>
    </dimmed>

    <dimmed v-if="isChargingJam">
      <charge-pv
          :topUpJam="isChargingJam"
          :remainJam="remainJam"
          @closeLayer="isChargingJam = false" />
    </dimmed>
  </div>
</template>

<script>
import store, { JOURNEY_TARGETING_VIEWABLE_START_DATE } from '@/store'
import { mapState, mapMutations, mapActions } from 'vuex'
import { format, startOfDay } from 'date-fns'
import { getAuthToken, removeAuthData, CommonStorage } from '@/libs/auth-helper'
import { STORAGE_KEY, storageManager } from '@/libs/storage-manager'
import { getDomains } from '@/api/domain'
import { getPluckData } from '@/api/pluck'
import { compare09aAzZ, getOffset, hasOverlapRanges, parseJwt } from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'

import Alert from '@/components/ui/Alert'
import Dimmed from '@/components/ui/Dimmed'
import ReportHeader from '@/components/report/Header'
import ReportLoading from '@/components/report/Loading'

import Marketing from '@/components/journey/Marketing'

import BaBookmarkListLayer from '@/components/targeting/BookmarkListLayer'
import ChatContainer from '@/components/docent/ChatContainer.vue'
import { SIGNIN_LINK } from '@/libs/constants'
import { getAIReportData, getSegmentMeta, createAIReportData, checkReportDuplicate, getAIReportPlan, getRemainReportCredit } from '@/api/ai'
import { saveReportPayment } from '@/api/card'

import AiReportPayment from '@/components/dashboard/AiReportPayment.vue'
import CheckoutView from '@/components/user-info/manage-plan/CheckoutView.vue'
import ChargePv from '@/components/user-info/manage-plan/ChargePv.vue'

import AppDialog from '@/components/ui/AppDialog.vue'
import { create } from 'vue-modal-dialogs'
import { getI18n } from '@/libs/i18n'
const aiReportOpenDialog = create(AppDialog, 'dialogType')

export default {
  components: {
    Alert,
    Dimmed,
    ReportHeader,
    ReportLoading,
    Marketing,
    BaBookmarkListLayer,
    AiReportPayment,
    CheckoutView,
    ChargePv,
    ChatContainer
  },
  beforeCreate () {
    let isDemo = false
    const authToken = getAuthToken()
    let tokenData = null
    if (authToken) {
      tokenData = parseJwt(authToken)
      if (tokenData && tokenData.identity) {
        if (tokenData.identity.is_demo) {
          isDemo = true
        }
      }
    }
    const userDataRequest = store.state.userData
      ? Promise.resolve(store.state.userData)
      : store.dispatch('storeUserData', { allowNoAuth: true })

    userDataRequest.then(userData => {
      if (userData) {
        storageManager.initUser(userData.userNum)
        const stickyDataId = storageManager.getItem(STORAGE_KEY.stickyDataId)
        let showStickyData = storageManager.getItem(STORAGE_KEY.showStickyData)
        let showNoPvDialog = storageManager.getItem(STORAGE_KEY.showNoPvDialog)

        if (showStickyData === null) {
          showStickyData = true
          storageManager.setItem(STORAGE_KEY.showStickyData, showStickyData)
        }

        if (showNoPvDialog === null) {
          showNoPvDialog = true
          storageManager.setItem(STORAGE_KEY.showNoPvDialog, showNoPvDialog)
        }
        store.commit('setStickyDataId', stickyDataId)
        store.commit('setShowStickyData', showStickyData)
        store.commit('setShowNoPvDialog', showNoPvDialog)
        store.commit('setIsDemo', isDemo)
        this.isDemo = isDemo
        // return store.dispatch('storePlanData')
      } else {
        removeAuthData()
      }
    }).catch(err => {
      console.error(err)
      removeAuthData()
    })
  },
  async created () {
    const authToken = getAuthToken()
    let tokenData = null
    if (authToken) {
      tokenData = parseJwt(authToken)
      if (tokenData && tokenData.identity) {
        if (tokenData.identity.is_demo) {
          if (this.$route.query.targetNodeId) {
            const targetNodeId = this.$route.query.targetNodeId
            const currentPage = this.$route.name

            // const newQuery = { ...this.$route.query }
            // delete newQuery.targetNodeId
            // this.$router.replace({ query: newQuery })

            const targetData = {
              direction: 'backward',
              pageFrom: currentPage,
              gUrlId: targetNodeId,
              // nodeData: this.listDataCache[this.selectedId],
              // isCustomNode: this.journeyOptions.isCustomNode,
              targetNodeKey: null,
              targetNodeId: targetNodeId
            }
            console.log('isdemo0000000')
            this.isDemoTarget = true
            this.setJourneyTargetingOptions(targetData)
          }
        } else {
          const newQuery = { ...this.$route.query }

          if (newQuery.targetNodeId) {
            delete newQuery.targetNodeId
            this.$router.replace({ query: newQuery })
          }
        }
      }
    }

    EventBus.$on('setSegmentPayment', () => {
      this.setSegmentPayment()
    })

    EventBus.$on('group-state', value => {
      this.groupState = value
    })

    store.dispatch('storeScrollbarWidth')

    EventBus.$on('journeyPluckChange', (data = {}) => {
      this.setJourneyPluck({
        start: data.start !== undefined ? data.start : this.journeyPeriod.start,
        end: data.end !== undefined ? data.end : this.journeyPeriod.end,
        device: data.device !== undefined ? data.device : this.journeyOptions.device,
        exist: data.exist !== undefined ? data.exist : this.journeyOptions.exist,
        matchType: data.matchType !== undefined ? data.matchType : this.journeyOptions.matchType,
        refer: data.refer !== undefined ? data.refer : this.journeyOptions.refer,
        aiRefererIds: data.aiRefererIds !== undefined ? data.aiRefererIds : this.journeyOptions.aiRefererIds,
        sid: data.sid !== undefined ? data.sid : this.journeyOptions.sid,
        subSid: data.subSid !== undefined ? data.subSid : this.journeyOptions.subSid,
        utm: data.utm !== undefined ? data.utm : this.journeyOptions.utm,
        survey: data.survey !== undefined ? data.survey : this.journeyOptions.survey,
        treeId: data.treeId
      })
      this.initJourney()
    })

    EventBus.$on('journeyOptionChange', (key, value, preventLoad, isBeforeInit) => {
      let options = this.journeyOptions
      options[key] = value
      if (key === 'utm') {
        options.survey = null
      } else if (key === 'survey') {
        options.utm = null
      }
      if (!isBeforeInit) {
        if (key === 'device') {
          options.refer = null
          options.aiRefererIds = null
          options.exist = null
        } else if (key === 'refer') {
          if (value !== null) {
            options.aiRefererIds = null
          }
          options.exist = null
        } else if (key === 'aiRefererIds') {
          if (Array.isArray(value) && value.length) {
            options.refer = null
          }
          options.exist = null
        }
      }
      this.setJourneyOptions(options)
    })

    EventBus.$on('sessionCount', (sessionCount) => {
      this.latestSessionCount = sessionCount
    })

    EventBus.$on('sessionTotalCount', (sessionCount) => {
      this.latestSessionTotalCount = sessionCount
    })

    EventBus.$on('settingUtm', (force, tag) => {
      this.onSettingUtm(force, tag)
    })

    EventBus.$on('onLoading', (value, from) => {
      if (from !== 'user-menu') {
        this.onLoading(value)
      }
    })

    EventBus.$on('onError', (error, code = 500) => {
      this.initialLoading = false
      this.loading = false
      this.setErrorData({ code, error })
    })

    EventBus.$on('reportViewChange', (value, oldValue) => {
      EventBus.$emit('onLoading', true)

      const query = { sid: this.sid }
      if (this.subSid) {
        query.subSid = this.subSid
        query.path = this.firstPath
      }

      if (value === 'dashboard') {
        this.$router.push({ name: 'dashboard', query: query }).catch(() => {
        })
      } else if (value === 'ranking') {
        this.setJourneyOptions({
          ...this.journeyOptions
        })
        this.$router.push({ name: 'ranking', query: query }).catch(() => {
        })
        this.initRanking()
      } else if (value === 'sitemap') {
        this.setJourneyOptions({
          ...this.journeyOptions
        })
        this.$router.push({ name: 'sitemap', query: query }).catch(() => {
        })
        this.initSitemap()
      } else if (value === 'journey') {
        this.setJourneyTargetingOptions(null)
        this.$router.push({ name: 'journey', query: query }).catch(() => {
        })
        this.initJourney()
      } else if (value === 'trend') {
        this.setJourneyOptions({
          ...this.journeyOptions
          // subSid: null,
          // firstPath: null
        })
        this.$router.push({ name: 'trend', query: query }).catch(() => {
        })
        this.initTrend()
      } else if (value === 'mine') {
        this.setJourneyOptions({
          ...this.journeyOptions
        })
        this.$router.push({ name: 'mine', query: query }).catch(() => {})
        this.initMine()
        // EventBus.$emit('loadPaneData')
      }
      this.utmCache = null
      CommonStorage.setItem(`reportView-${this.subSid || this.sid}`, value)
    })

    EventBus.$on('journeyPeriodChange', (value) => {
      this.$emit('panePeriodChange', value)

      let isCustomNode = this.isCustomNode
      if (this.isCustomNode) {
        const beuCustomRegDateStr = this.domain.beuCustomRegDate ? format(this.domain.beuCustomRegDate, 'YYYY-MM-DD') : null
        if (beuCustomRegDateStr) {
          if (value.start < beuCustomRegDateStr) {
            isCustomNode = false
          }
        } else {
          isCustomNode = false
        }
      } else {
        isCustomNode = false
      }

      this.setJourneyOptions({
        ...this.journeyOptions,
        refer: null,
        aiRefererIds: null,
        exist: null,
        utm: null,
        survey: null,
        isContainPaid: this.isContainPaid(value),
        isCustomNode: isCustomNode
      })
      this.utmCache = null
      this.setJourneyPeriod(value)
    })

    await this.getAIReportCheck()

    if (this.reportView === 'ranking') {
      this.initRanking()
    } else if (this.reportView === 'sitemap') {
      this.initSitemap()
    } else if (this.reportView === 'journey') {
      if (this.pluckCode) {
        getPluckData({ sid: this.sid, pluck: this.pluckCode }).then(response => {
          this.setJourneyPluck(response)
          EventBus.$emit('journeyPeriodChange', {
            ...this.journeyPeriod,
            start: response.start,
            end: response.end
          })
          this.initJourney()
        }).catch(error => {
          if (error.message === 'No Pluck found') {
            EventBus.$emit('onError', error, 'pluck404')
          } else if (error.message === 'No Permission found') {
            EventBus.$emit('onError', error, 'pluck401')
          } else if (error.message === 'No Domain found') {
            if (this.userData) {
              EventBus.$emit('onError', error, 404)
            } else {
              window.location.href = SIGNIN_LINK
            }
          } else {
            EventBus.$emit('onError', error, 500)
          }
        })
      } else if (this.aiReportData) {
        if (this.aiReportData.treeId) {
          this.setJourneyPluck(this.aiReportData)
        }
        EventBus.$emit('journeyPeriodChange', {
          ...this.journeyPeriod,
          start: this.aiReportData.startDateStr,
          end: this.aiReportData.endDateStr
        })
        this.initJourney()
      } else {
        this.initJourney()
      }
    } else if (this.reportView === 'trend') {
      this.initTrend()
    } else if (this.reportView === 'mine') {
      this.initMine()
    }

    this.storeBookmarkCount()

    getAIReportPlan().then(resp => {
      this.reportPlan = resp
    })
    this.getAiReportCredit()
  },
  destroyed () {
    EventBus.$off('journeyPeriodChange')
    EventBus.$off('journeyOptionChange')
    EventBus.$off('reportViewChange')
    EventBus.$off('group-state')
    EventBus.$off('setSegmentPayment')
  },
  methods: {
    ...mapMutations([
      'setErrorData',
      'setJourneyOptions',
      'setJourneyPeriod',
      'setJourneyPluck',
      'setJourneyTargetingOptions',
      'setBookmarkListLayerOptions',
      'setDomain',
      'setOriginJourneyData',
      'setAIReportData'
    ]),
    ...mapActions([
      'storeBookmarkCount'
    ]),
    getAiReportCredit () {
      getRemainReportCredit().then(res => { this.aiReportCredit = res })
    },
    getAIReportCheck () {
      return new Promise(async (resolve, reject) => {
        if (this.aiReportId) {
          getAIReportData(this.aiReportId, true).then(res => {
            if (this.aiReportTreeId) {
              res.treeId = this.aiReportTreeId
            }
            this.setAIReportData(res)
            resolve()
          })
        } else {
          resolve()
        }
      })
    },
    initRanking () {
      const journeyOptionNow = this.journeyOptions
        ? { ...this.initialJourneyOptions, ...this.journeyOptions }
        : this.initialJourneyOptions

      if (this.aiReportData) {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.aiReportData.sid,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.aiReportData.device !== undefined ? this.aiReportData.device : this.initialJourneyOptions.device,
          refer: this.aiReportData.refer !== undefined ? this.aiReportData.refer : this.initialJourneyOptions.refer,
          aiRefererIds: this.aiReportData.aiRefererIds !== undefined ? this.aiReportData.aiRefererIds : this.initialJourneyOptions.aiRefererIds,
          exist: this.aiReportData.exist !== undefined ? this.aiReportData.exist : this.initialJourneyOptions.exist,
          utm: this.aiReportData.utm !== undefined ? this.aiReportData.utm : this.initialJourneyOptions.utm,
          survey: this.aiReportData.survey !== undefined ? this.aiReportData.survey : this.initialJourneyOptions.survey
        })
        this.setJourneyPeriod({
          ...this.journeyPeriod,
          start: this.aiReportData.startDateStr,
          end: this.aiReportData.endDateStr
        })
      } else {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.sid,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.initialJourneyOptions.device,
          refer: this.initialJourneyOptions.refer,
          aiRefererIds: this.initialJourneyOptions.aiRefererIds,
          exist: this.initialJourneyOptions.exist,
          utm: this.initialJourneyOptions.utm,
          survey: this.initialJourneyOptions.utm,
          subSid: this.subSid,
          firstPath: this.firstPath
        })
      }
      this.loadDomains()
    },
    initSitemap () {
      const journeyOptionNow = this.journeyOptions
        ? { ...this.initialJourneyOptions, ...this.journeyOptions }
        : this.initialJourneyOptions

      this.setJourneyOptions({
        ...journeyOptionNow,
        domain: this.journeyOptions ? this.journeyOptions.domain : null,
        sid: this.sid,
        matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
        device: this.initialJourneyOptions.device,
        refer: this.initialJourneyOptions.refer,
        aiRefererIds: this.initialJourneyOptions.aiRefererIds,
        exist: this.initialJourneyOptions.exist,
        utm: this.initialJourneyOptions.utm,
        survey: this.initialJourneyOptions.survey,
        subSid: this.subSid,
        firstPath: this.firstPath
      })
      this.loadDomains()
      EventBus.$emit('onLoading', 'init')
    },
    initJourney () {
      const journeyOptionNow = this.journeyOptions
        ? { ...this.initialJourneyOptions, ...this.journeyOptions }
        : this.initialJourneyOptions
      if (this.journeyPluck) {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.sid,
          subSid: this.subSid,
          firstPath: this.firstPath,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.journeyPluck.device !== undefined ? this.journeyPluck.device : this.initialJourneyOptions.device,
          refer: this.journeyPluck.refer !== undefined ? this.journeyPluck.refer : this.initialJourneyOptions.refer,
          aiRefererIds: this.journeyPluck.aiRefererIds !== undefined ? this.journeyPluck.aiRefererIds : this.initialJourneyOptions.aiRefererIds,
          exist: this.journeyPluck.exist !== undefined ? this.journeyPluck.exist : this.initialJourneyOptions.exist,
          utm: this.journeyPluck.utm !== undefined ? this.journeyPluck.utm : this.initialJourneyOptions.utm,
          survey: this.journeyPluck.survey !== undefined ? this.journeyPluck.survey : this.initialJourneyOptions.survey
        })
      } else if (this.aiReportData && !this.aiReportData.treeId) {
        const reportType = this.aiReportData.type
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.aiReportData.sid,
          subSid: this.aiReportData.subSid,
          firstPath: this.firstPath,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.aiReportData.device !== undefined ? this.aiReportData.device : this.initialJourneyOptions.device,
          refer: this.aiReportData.refer !== undefined ? this.aiReportData.refer : this.initialJourneyOptions.refer,
          aiRefererIds: this.aiReportData.aiRefererIds !== undefined ? this.aiReportData.aiRefererIds : this.initialJourneyOptions.aiRefererIds,
          exist: this.aiReportData.exist !== undefined ? this.aiReportData.exist : this.initialJourneyOptions.exist,
          utm: this.aiReportData.utm !== undefined ? this.aiReportData.utm : this.initialJourneyOptions.utm,
          survey: this.aiReportData.survey !== undefined ? this.aiReportData.survey : this.initialJourneyOptions.survey
        })

        this.setJourneyPeriod({
          ...this.journeyPeriod,
          start: this.aiReportData.startDateStr,
          end: this.aiReportData.endDateStr
        })

        if (reportType === 'target') {
          this.setJourneyTargetingOptions({
            direction: this.aiReportData.direction !== undefined ? this.aiReportData.direction : 'backward',
            pageFrom: 'aiReport',
            gUrlId: this.aiReportData.targetNodeId,
            targetNodeKey: this.aiReportData.matchType === 'simple' ? null : this.aiReportData.targetNodeKey,
            targetNodeId: this.aiReportData.targetNodeId,
            maxSeq: this.aiReportData.seq !== undefined ? this.aiReportData.seq - 1 : null
          })
        }
      } else {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.sid,
          subSid: this.subSid,
          firstPath: this.firstPath,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.initialJourneyOptions.device,
          refer: this.initialJourneyOptions.refer,
          aiRefererIds: this.initialJourneyOptions.aiRefererIds,
          exist: this.initialJourneyOptions.exist,
          utm: this.initialJourneyOptions.utm,
          survey: this.initialJourneyOptions.survey
        })
      }
      this.loadDomains()
    },

    initTrend () {
      const journeyOptionNow = this.journeyOptions
        ? { ...this.initialJourneyOptions, ...this.journeyOptions }
        : this.initialJourneyOptions

      if (this.aiReportData) {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.aiReportData.sid,
          subSid: this.aiReportData.subSid,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.aiReportData.device !== undefined ? this.aiReportData.device : this.initialJourneyOptions.device,
          refer: this.aiReportData.refer !== undefined ? this.aiReportData.refer : this.initialJourneyOptions.refer,
          aiRefererIds: this.aiReportData.aiRefererIds !== undefined ? this.aiReportData.aiRefererIds : this.initialJourneyOptions.aiRefererIds,
          exist: this.aiReportData.exist !== undefined ? this.aiReportData.exist : this.initialJourneyOptions.exist,
          utm: this.aiReportData.utm !== undefined ? this.aiReportData.utm : this.initialJourneyOptions.utm,
          survey: this.aiReportData.survey !== undefined ? this.aiReportData.survey : this.initialJourneyOptions.survey
        })

        this.setJourneyPeriod({
          ...this.journeyPeriod,
          start: this.aiReportData.startDateStr,
          end: this.aiReportData.endDateStr
        })
      } else {
        this.setJourneyOptions({
          ...journeyOptionNow,
          domain: this.journeyOptions ? this.journeyOptions.domain : null,
          sid: this.sid,
          subSid: this.subSid,
          firstPath: this.firstPath,
          matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
          device: this.initialJourneyOptions.device,
          refer: this.initialJourneyOptions.refer,
          aiRefererIds: this.initialJourneyOptions.aiRefererIds,
          exist: this.initialJourneyOptions.exist,
          utm: this.initialJourneyOptions.utm,
          survey: this.initialJourneyOptions.survey
        })
      }
      this.loadDomains()
    },
    initMine () {
      const journeyOptionNow = this.journeyOptions
        ? { ...this.initialJourneyOptions, ...this.journeyOptions }
        : this.initialJourneyOptions
      this.setJourneyOptions({
        ...journeyOptionNow,
        domain: this.journeyOptions ? this.journeyOptions.domain : null,
        sid: this.sid,
        matchType: this.journeyOptions ? this.journeyOptions.matchType : null,
        device: this.initialJourneyOptions.device,
        refer: this.initialJourneyOptions.refer,
        aiRefererIds: this.initialJourneyOptions.aiRefererIds,
        exist: this.initialJourneyOptions.exist,
        utm: this.initialJourneyOptions.utm,
        survey: this.initialJourneyOptions.survey
      })
      this.loadDomains()
    },
    loadDomains () {
      if (this.domainLoading) {
        getDomains(this.sid, false).then(result => {
          result.sort(compare09aAzZ)
          result.forEach(item => {
            item.existSubdomains = false
            if (item.subDomains.length > 0) {
              item.existSubdomains = true
            }
            item.openSubDomains = false
          })

          for (const domain of result) {
            if (domain.mainDomain && domain.shared !== 'shared') {
              domain.timezone = getOffset(domain.timezoneName)
              this.domains.push(domain)
              break
            }
          }

          for (const domain of result) {
            if (!domain.mainDomain || domain.shared === 'shared') {
              domain.timezone = getOffset(domain.timezoneName)
              this.domains.push(domain)
            }
          }

          // this.domains = result

          for (const domain of result) {
            if ([domain.orgSid, domain.sid].includes(this.sid)) {
              this.setJourneyOptions({
                ...this.journeyOptions,
                domain: domain.domain,
                sid: this.sid,
                matchType: domain.matchType
              })
              this.setDomain(domain)
              this.storeBookmarkCount()
              store.dispatch('storePlanData')

              this.domainLoading = false
              break
            }
          }
          // this.domains = result
          if (this.domainLoading) {
            EventBus.$emit('onError', new Error('No Domain'), 404)
          }
        })
      }
    },

    onLoading (value) {
      if (value === 'init') {
        this.initialLoading = false
      } else {
        this.loading = value
      }
    },

    isContainPaid (period) {
      if (!this.journeyPeriod || !this.planData) {
        return false
      }
      const needle = {
        start: startOfDay(period.start),
        end: startOfDay(period.end)
      }
      return hasOverlapRanges(needle, this.planData.paidRange)
    },

    onSettingUtm (force, tag) {
      if ((this.utmEnabled || this.surveyEnabled) && !force) {
        EventBus.$emit('onLoading', true)
        EventBus.$emit('journeyOptionChange', 'utm', null)
        EventBus.$emit('journeyOptionChange', 'survey', null)
      } else {
        this.utmCache = this.utmCache || this.journeyOptions.utm
        this.settingUtm = tag
      }
    },

    onSetUtm (selectedUtm) {
      this.utmCache = selectedUtm
      this.settingUtm = false
      if (JSON.stringify(this.journeyOptions.utm) !== JSON.stringify(selectedUtm)) {
        EventBus.$emit('onLoading', true)

        setTimeout(() => {
          EventBus.$emit('journeyOptionChange', 'utm', selectedUtm)
        }, 300)
      }
    },

    onSetSurvey (selectedSurvey) {
      EventBus.$emit('onLoading', true)
      this.utmCache = null
      setTimeout(() => {
        EventBus.$emit('journeyOptionChange', 'survey', selectedSurvey)
      }, 300)
    },

    onChangeDateToTargetable () {
      EventBus.$emit('journeyPeriodChange', {
        ...this.journeyPeriod,
        start: JOURNEY_TARGETING_VIEWABLE_START_DATE
      })
      this.isGivingChanceToChangeDateForTargeting = false
      this.setJourneyTargetingOptions(null)
    },
    onCancelChangeDateToTargetable () {
      this.setJourneyTargetingOptions(null)
    },

    closeMarketing () {
      this.settingUtm = false
    },
    getPlan () {
      const param = { isPaid: true, promptType: 'single', reportType: 'segments' }
      const plan = this.reportPlan.filter(obj =>
        Object.keys(param).every(key => obj[key] === param[key])
      )
      return plan
    },
    setSegmentPayment () {
      if (this.getPriceMethod()) {
        this.getSegmentMetaData()
      } else {
        this.successTossPayment()
      }
    },
    getPriceMethod () {
      const plan = this.getPlan()
      return plan[0].price
    },
    getSegmentMetaData () {
      let type = this.reportView
      if (this.journeyTargetingOptions) type = 'target'

      const requestData = {
        type,
        sid: this.domain.sid
      }
      getSegmentMeta(requestData).then(res => {
        this.segmentMetaList = res
        this.setPayment = !this.setPayment
      })
    },
    successTossPayment (payData) {
      this.reportPaymentType = payData.variantKey
      this.reportPaymentId = payData.paymentId
      this.openTossView = false
      this.onCreateSegmentAIReport()
    },
    onCreateSegmentAIReport (useCredit = false) {
      const data = this.getParams()
      data.reportPaymentId = this.reportPaymentId
      data.reportPaymentType = this.reportPaymentType
      if (useCredit) {
        data.isUseAIReportCredit = true
      }
      data.paid = true
      createAIReportData(data, true).then(res => {
        this.aiReportProcessing = true
        aiReportOpenDialog('report.reportRequest.success')
        this.aiReportProcessing = false
      }).catch(err => {
        if (err.message === 'sysError') {
          aiReportOpenDialog('report.reportRequest.sysError')
        }
      }).finally(() => {
        this.resetReportState()
      })
    },
    checkDuplicate (lang, creditReport = null) {
      const requestData = this.getParams()
      requestData.lang = lang
      requestData.checkType = 'segment'
      checkReportDuplicate(requestData, true).then(res => {
        if (res) {
          aiReportOpenDialog('report.reportRequest.duplicate')
          this.setPayment = false
        } else {
          if (creditReport) {
            this.requestPayment(creditReport)
            this.setPayment = false
          }
        }
      })
    },
    resetReportState () {
      this.selectedReportInfo = {}
      this.tossPrice = null
      this.variantKey = null
      this.reportPaymentType = null
      this.reportPaymentId = null
    },
    requestPayment (payload) {
      this.selectedReportInfo = {
        title: payload.title,
        reportData: payload.reportData,
        lang: payload.lang
      }
      if (payload.paymentType === 'justCard' || payload.paymentType === 'justWire') {
        this.tossPrice = payload.price
        this.openTossPayment(payload.paymentType)
      } else if (payload.paymentType === 'aiReportCredit') {
        const useCredit = true
        this.onCreateSegmentAIReport(useCredit)
      } else {
        const params = {
          variantKey: payload.paymentType,
          serviceType: 'ba',
          price: payload.paymentType === 'jam' ? payload.price : payload.price * 1.1
        }
        saveReportPayment(params)
          .then(resp => {
            if (resp) {
              this.reportPaymentType = payload.paymentType
              this.reportPaymentId = resp._id
              this.onCreateSegmentAIReport()
            } else {
              this.resetReportState()
            }
          }).catch(err => {
            console.log(err)
            this.resetReportState()
          })
      }
    },
    openTossPayment (variantKey) {
      this.variantKey = variantKey
      this.openTossView = true
    },
    cancelTossPayment () {
      this.openTossView = false
      this.resetReportState()
    },
    onChargeJam (remainJam) {
      this.setPayment = false
      this.remainJam = remainJam
      this.isChargingJam = true
    },
    getListIndex (total) {
      let setList = []

      for (let i = 0; i < 3; i++) {
        let idx = Math.floor(Math.random() * 10) % total
        while (setList.some(c => c === idx)) {
          idx = Math.floor(Math.random() * 10) % total
        }
        setList.push(idx)
      }
      return setList
    },
    setList () {
      const randomList = this.getListIndex(this.segmentMetaList.length)
      return randomList.map(item => { return this.segmentMetaList[item] })
      // if (this.segmentMetaList.length <= 5) {
      //   return this.segmentMetaList
      // } else {
      //   const randomList = this.getListIndex(this.segmentMetaList.length)
      //   return randomList.map(item => { return this.segmentMetaList[item] })
      // }
    },
    getParams () {
      const reportView = this.reportView
      const title = this.selectedReportInfo.title || ''
      const lang = this.selectedReportInfo.lang || getI18n().locale
      const data = {
        parentSid: this.parentSid,
        title,
        lang,
        type: reportView,
        journeyOptions: {
          ...this.journeyOptions
        },
        journeyPeriod: {
          ...this.journeyPeriod
        }
      }

      if (this.selectedReportInfo.reportData) {
        data.journeyOptions = {
          ...this.journeyOptions,
          ...this.selectedReportInfo.reportData.options
        }
        data.journeyPeriod.start = this.selectedReportInfo.reportData.options.startDateStr
        data.journeyPeriod.end = this.selectedReportInfo.reportData.options.endDateStr
      }

      if (this.journeyTargetingOptions) {
        data.journeyOptions = {
          ...this.journeyOptions,
          ...this.journeyTargetingOptions,
          direction: this.journeyTargetingOptions.direction || 'backward',
          targetNodeId: this.journeyOptions.isCustomNode ? this.journeyTargetingOptions.targetNodeKey : this.journeyTargetingOptions.gUrlId,
          isCustomNode: this.journeyOptions.isCustomNode,
          targetNodeKey: this.journeyOptions.isCustomNode || this.journeyOptions.matchType === 'exact' ? this.journeyTargetingOptions.targetNodeKey : null,
          startFromHead: this.targetStartFromHead
        }

        if (data.journeyOptions.rank) {
          delete data.journeyOptions.rank
        }
        if (data.journeyOptions.activatedId) {
          delete data.journeyOptions.activatedId
        }

        data.type = 'target'
      }

      const removeFieldList = ['referrerItems', 'isContainPaid', 'listSize']
      for (let i = 0; i < removeFieldList.length; i++) {
        const removeField = removeFieldList[i]
        if (data.journeyOptions[removeField]) {
          delete data.journeyOptions[removeField]
        }
      }
      return data
    }
  },
  watch: {
    '$route.query.sid' () {
      if (this.reportView === 'ranking') {
        this.initRanking()
      } else if (this.reportView === 'sitemap') {
        this.initSitemap()
      } else if (this.reportView === 'journey') {
        this.initJourney()
      } else if (this.reportView === 'trend') {
        this.initTrend()
      } else if (this.reportView === 'mine') {
        this.initMine()
      }
    },
    initialLoading (value) {
      if (!value) {
        setTimeout(() => {
          this.viewWrap = true
        }, 2000)
      }
    },
    planData (value) {
      if (value) {
        this.setJourneyOptions({
          ...this.journeyOptions,
          isContainPaid: this.isContainPaid(this.journeyPeriod)
        })
      }
    },
    journeyTargetingOptions (value, oldValue) {
      if (value === 'start-date-is-not-viewable') {
        this.isGivingChanceToChangeDateForTargeting = true
      } else if (oldValue === 'start-date-is-not-viewable') {
        this.isGivingChanceToChangeDateForTargeting = false
      } else if (value === null && oldValue !== null && this.reportView === 'journey') {
        this.$nextTick(() => {
          EventBus.$emit('loadPaneData')
        })
      }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'gnbMinimized',
      'journeyOptions',
      'journeyPeriod',
      'journeyPluck',
      'journeyTargetingOptions',
      'bookmarkListLayerOptions',
      'isSampling',
      'originJourneyData',
      'domain',
      'aiReportData',
      'targetStartFromHead',
      'showAiDocent'
    ]),
    isCustomNode () {
      return this.journeyOptions ? this.journeyOptions.isCustomNode : false
    },
    customRegDate () {
      const beuCustomRegDateStr = this.domain.beuCustomRegDate ? format(this.domain.beuCustomRegDate, 'YYYY-MM-DD') : null
      return beuCustomRegDateStr
    },
    isSurvey () {
      return this.journeyOptions.survey
    },
    reportView () {
      return this.$route.name
    },
    sid () {
      return this.$route.query.sid
    },
    subSid () {
      return this.$route.query.subSid
    },
    parentSid () {
      return 's' + this.$route.query.sid
    },
    firstPath () {
      return this.$route.query.path === '' ? '' : this.$route.query.path
    },
    fullPath () {
      return this.$route.fullPath.split('?')[0]
    },
    pluckCode () {
      return this.$route.query.pluck || null
    },
    aiReportId () {
      return this.$route.query.aiReportId || null
    },
    aiReportTreeId () {
      return this.$route.query.aiReportTreeId || null
    },
    utmEnabled () {
      return !!this.journeyOptions.utm
    },
    surveyEnabled () {
      return !!this.journeyOptions.survey
    },

    givingChanceToChangeDateDescription () {
      return this.$t('journey.targeting.changeDate.description') + [
        '<p class="targeting-date-change-preview">',
        JOURNEY_TARGETING_VIEWABLE_START_DATE + ' ~ ' + this.journeyPeriod.end,
        '</p>'
      ].join('')
    },
    reportInfoForAIReport () {
      return {
        promptType: 'single',
        type: this.journeyTargetingOptions ? 'target' : this.reportView,
        ...this.journeyOptions,
        startDateStr: this.journeyPeriod.start,
        endDateStr: this.journeyPeriod.end
      }
    },
    getJamPrice () {
      const plan = this.getPlan()
      return plan[0].jamPrice
    },
    getPrice () {
      const plan = this.getPlan()
      return plan[0].price
    }
  },
  data () {
    return {
      isDemoTarget: false,
      initialLoading: true,
      loading: true,
      domainLoading: true,
      viewWrap: false,
      initialJourneyOptions: {
        device: null,
        matchType: null,
        sid: null,
        domain: null,
        exist: null,
        refer: null,
        aiRefererIds: null,
        listSize: { x: 3, y: 3 },
        utm: null,
        survey: null,
        isCustomNode: false
      },
      domains: [],

      settingUtm: false,
      utmCache: null,
      latestSessionCount: {},
      latestSessionTotalCount: 0,

      isGivingChanceToChangeDateForTargeting: false,

      groupState: false,
      // segment-ai
      reportPlan: null,
      reportPaymentType: null,
      reportPaymentId: null,
      isChargingJam: false,
      remainJam: true,
      openTossView: false,
      setPayment: false,
      aiReportProcessing: false,
      segmentMetaList: [],
      selectedReportInfo: {},
      aiReportCredit: null
    }
  }
}
</script>

<style lang="scss" scoped>
.journey_layout {
  width: 100%;
  min-width: 1250px;
  height: 100%;
  // min-height: 830px;

  .header-wrap {
    transition: transform 0.3s ease,
    margin-bottom 0.3s ease;
  }

  .content-wrap {
    position: relative;
    height: 100%;
  }

  .view-wrap {
    height: calc(100% - 106px);
    transition: height 0.3s ease;
  }

  .loading--mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
    background-color: #f1f1f1;
  }

  .loading--wrap {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
  }

  &.minimized {
    .header-wrap {
      margin-bottom: -43px;
      transform: translateY(-43px);
    }

    .view-wrap {
      height: calc(100% - 10px);
    }
  }

  &.expanded {
    .view-wrap {
      height: calc(100% - 261px);
    }

    .loading--wrap {
      top: 261px;
      height: calc(100% - 261px);
    }
  }
  &.grouping {
    .ai-data-button {
      // transition: right 500ms;
      right: 270px;
    }
    & /deep/ .temp-ai-data-button {
      right: 440px
    }
  }
}

.fade-enter-active, .fade-leave-active {
  opacity: 1;
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to{
  opacity: 0;
}

.alert {
  & /deep/ .targeting-date-change-preview {
    font-size: 14px;
    color: #ec0047;
    line-height: 1;
    margin: 20px 0 -10px;
  }
}
</style>
